import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import Message from "../../log/Message";
import {MessageType} from "../../log/MessageInterface";
import AbstractDialog, {AbstractDialogProperties, AbstractDialogState} from "./AbstractDialog";
import Translate from "../../translation/Translate";
import Icon from "../../components/Icon";
import {AuthProvider, Information, UserStorage} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import StorageItem from "../../StorageItem";
import {InputSwitch} from "primereact/inputswitch";
import LogoutDialogFields from "../fields/LogoutDialogFields";
import portalConfiguration from "../../configuration/PortalConfiguration";
import azure from "../../../../resources/images/oauth/azure.svg";
import FunctionHelper from "../../helper/FunctionHelper";

export interface LogoutDialogState extends AbstractDialogState {
	logoutProvider: boolean;
}

export class LogoutDialogDefinition extends AbstractDialog<AbstractDialogProperties, LogoutDialogState> {
	constructor(props: Readonly<AbstractDialogProperties>) {
		super(props);

		this.state = {
			...this.state,
			logoutProvider: false
		}
	}

	protected getProvider(): AuthProvider | undefined {
		let portalInfo: Information = this.props.context.info;

		let providerName: string | null = localStorage.getItem(StorageItem.PROVIDER);
		for (let oauthProvider of FunctionHelper.applyValue(portalInfo.oauth, [])) {
			if (oauthProvider.name === providerName) {
				return oauthProvider;
			}
		}

		return undefined;
	}

	public renderDialog(): ReactNode {
		let provider: AuthProvider | undefined = this.getProvider();

		let classList: Array<string> = ["logout-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<fieldset className="p-col-12 p-grid">
						<div className="dialog-message">
							<Translate {...this.props} data="logout.message" namespace="dialog"/>
						</div>
					</fieldset>
					{FunctionHelper.exists(provider?.logout?.url) &&
						<fieldset className="p-col-12 p-grid">
							<label className="p-col-1"/>
							<div className="p-col-11 switch-container">
								<InputSwitch checked={this.state.logoutProvider} inputId="control-provider"
											 onChange={(e) =>
												 this.onChange(LogoutDialogFields.PROVIDER, e.value)
											 }/>
								<label className="switch-label" htmlFor="control-provider">
									<Translate {...this.props} data="logout.provider" namespace="dialog"/>
								</label>
							</div>
						</fieldset>
					}
				</div>
			</Dialog>
		);
	}

	protected renderHeaderIcon(): ReactNode {
		let portalInfo: Information = this.props.context.info;

		switch (portalInfo.localAuth?.userStorage) {
			case UserStorage.AzureAd:
				return <img src={azure} alt="Microsoft"/>;
			case UserStorage.Ldap:
				return <Icon icon={"icon-user ldap"}/>;
			case UserStorage.Database:
				return <Icon icon={"icon-user database"}/>;
		}

		return <Icon icon={"icon-user"}/>;
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					{this.renderHeaderIcon()}
					<Translate {...this.props} data="logout.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
			</div>
		);
	}

	/**
	 * Render the dialog footer
	 */
	protected renderFooter(): ReactNode {
		return (
			<div>
				<Button label={Translate.value("buttons.logout")} disabled={!this.isValid()}
						icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-logout")}
						className="primary-button" onClick={() => this.submit()}/>
				<Button label={Translate.value("buttons.cancel")} icon="icon icon-close" onClick={() => this.hide()}
						className="dialog-button"/>
			</div>
		);
	}

	protected isValid(): boolean {
		if (!this.mounted || !this.props.visible) {
			return false;
		}

		return !this.state.submitted;
	}

	protected onChange(field: LogoutDialogFields, value: any): void {
		switch (field) {
			case LogoutDialogFields.PROVIDER:
				this.setState({
					logoutProvider: value
				})
				break;
		}
	}

	protected replaceVariables(value: string, provider: AuthProvider): string {
		switch (value) {
			case "{redirectUrl}":
				return portalConfiguration.portalUrl.toString();
			case "{clientId}":
				return provider.clientId!;
			case "{appId}":
				return provider.applicationId!;
		}

		return value;
	}

	protected async submit(): Promise<void> {
		let provider: AuthProvider | undefined = this.getProvider();

		this.setState({
			exception: undefined,
			submitted: true
		});

		try {
			await this.props.context.session.close();

			if (this.state.logoutProvider) {
				if (FunctionHelper.exists(provider?.logout?.url)) {
					let url: URL = new URL(provider!.logout!.url!.replaceAll("&amp;", "&"));

					for (let [key, value] of url.searchParams.entries()) {
						url.searchParams.set(key, this.replaceVariables(value, provider!));
					}

					window.location.href = url.toString();
				}
				return;
			}
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}
			return;
		}

		let message: Message = new Message("success.authentication.logout", MessageType.SUCCESS);
		this.props.context.getPage().addMessage(message);
		this.hide();
		window.location.reload();
	}
}

const LogoutDialog = React.memo(LogoutDialogDefinition,
	(prevProps: Readonly<AbstractDialogProperties>, nextProps: Readonly<AbstractDialogProperties>) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default LogoutDialog;